import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import PageHeader from '../../components/PageHeader';
import LeadsService from '../../services/LeadsService';
import UsersService from '../../services/UsersService';
import Select from 'react-select';
import ProductService from '../../services/ProductService';
import { leadStatusOptions, leadSourceOptions, empoyeeCountOptions } from '../../utils/OptionUtils';
import { Country, State, City } from 'country-state-city';
import SelectTag from '../../components/SelectTag';

const EditLead = () => {
    const { id } = useParams();
    const { register, handleSubmit, formState: { errors }, reset, control, setValue } = useForm();
    const [AssigneeList, setAssigneeList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const leadResponse = await LeadsService.get(id);
            const assigneeResponse = await UsersService.list();
            const leadData = leadResponse.data.data;
            const assignees = assigneeResponse.data.data;

            if (leadData) {

                let assignedToData = {};
                if (leadData.assignedTo) {
                    assignedToData = {
                        value: leadData.assignedTo.id,
                        label: leadData.assignedTo.name
                    };
                }

                setAssigneeList(
                    assignees.map((assignee) => ({
                        value: assignee.id,
                        label: assignee.name + '-' + assignee.email,
                    }))
                );
                if (leadData.country && leadData.state) {
                    setSelectedCountry(leadData.country);

                    const states = State.getStatesOfCountry(leadData.country).map((state) => ({
                        value: state.isoCode,
                        label: state.name,
                    }));
                    setStateList(states);
                    const cities = City.getCitiesOfState(leadData.country, leadData.state).map((city) => ({
                        value: city.name,
                        label: city.name,
                    }));
                    setCityList(cities);
                }

                reset({
                    ...leadData,
                    assigned_to: assignedToData,
                });
            } else {
                toast.error('Lead data is empty or undefined');
            }
        } catch (error) {
            console.error('Error fetching data', error);
            toast.error('Error fetching data');
        }
    }, [id, reset]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSubmit = async (data) => {
        const requestBody = {
            data: {
                ...data,
                assigned_to: data.assigned_to?.value || null,
            }
        };
        try {
            await LeadsService.update(id, requestBody);
            toast.success('Lead updated successfully!');
        } catch (error) {
            toast.error('Failed to update lead: ' + (error.message || 'Unknown error'));
        }
    };

    // Function to only allow numbers
    const handleInputChange = (e, maxLength) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, maxLength);
    };
    const fetchProducts = useCallback(async () => {
        try {
            const response = await ProductService.list();
            if (response.status === 200) {
                const productData = response?.data?.data;
                const options = productData.map((product) => ({
                    id: product.id,
                    name: product.name,
                }));
                setProductList(options);
            }
        } catch (error) {
            console.log(error)
        }
    }, []);
    const fetchCountries = useCallback(async () => {
        const countries = Country.getAllCountries().map((country) => ({
            value: country.isoCode,
            label: country.name,
        }));
        setCountryList(countries);
    }, []);
    const handleCountryChange = (selectedCountry) => {
        if (selectedCountry) {
            setSelectedCountry(selectedCountry);
            const states = State.getStatesOfCountry(selectedCountry.value).map(state => ({
                value: state.isoCode,
                label: state.name
            }));
            setStateList(states);
            setValue("state", null);
        } else {
            setStateList([]);
            setValue("state", null);
        }
    };

    const handleStateChange = (selectedState) => {
        if (selectedState && selectedCountry) {
            const cities = City.getCitiesOfState(selectedCountry.value || selectedCountry, selectedState.value).map(city => ({
                value: city.name,
                label: city.name
            }));
            setCityList(cities);
            setValue("city", null);
        } else {
            setCityList([]);
            setValue("city", null);
        }
    };
    useEffect(() => {
        fetchProducts();
        fetchCountries();
    }, [fetchProducts, fetchCountries]);

    return (
        <>
            <ToastContainer />
            <AppSidebar />
            <div className="main-content">
                <AppHeader />
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10 col-xl-12">
                            <PageHeader title="Edit Lead" button_label1="Back"
                                link_to1="/leads" className1="btn btn-primary btn-sm" />
                            <div className="card p-4">
                                <form className="row g-3" onSubmit={handleSubmit(onSubmit)}>
                                    <div className="col-md-4">
                                        <label className="form-label">Status</label>
                                        <Controller
                                            name="status"
                                            control={control}
                                            rules={{ required: "The status field is required" }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={leadStatusOptions}
                                                    placeholder="Select status"
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    value={leadStatusOptions.find(option => option.value === field.value) || null}
                                                    onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                                />
                                            )}
                                        />
                                        {errors?.status && <p className="invalid-feedback">{errors?.status?.message}</p>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Source</label>
                                        <Controller
                                            name="source"
                                            control={control}
                                            rules={{ required: "The source field is required" }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={leadSourceOptions}
                                                    placeholder="Select source"
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    value={leadSourceOptions.find(option => option.value === field.value) || null}
                                                    onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                                />
                                            )}
                                        />
                                        {errors?.source && <p className="invalid-feedback">{errors?.source?.message}</p>}
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Assigned To</label>
                                            <Controller
                                                name="assigned_to"
                                                control={control}
                                                defaultValue={null}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={AssigneeList}
                                                        closeMenuOnSelect={true}
                                                        isClearable={true}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('name', {
                                                required: "The name field is required.",
                                                minLength: { value: 2, message: "The name must be at least 2 characters." },
                                            })}
                                        />
                                        {errors?.name && <p className="text-danger">{errors?.name?.message}</p>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            {...register('email', {
                                                required: "The email field is required.",
                                                pattern: {
                                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                    message: "Email must be a valid email address.",
                                                },
                                            })}
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        />
                                        {errors?.email && <p className="text-danger">{errors?.email?.message}</p>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Phone</label>
                                        <input
                                            type="tel"
                                            maxLength={10}
                                            {...register('phone', {
                                                required: "The phone number field is required.",
                                                minLength: {
                                                    value: 10,
                                                    message: "The phone number must be a 10-digit number."
                                                },
                                                maxLength: {
                                                    value: 10,
                                                    message: "The phone number must be a 10-digit number."
                                                },
                                                pattern: {
                                                    value: /^[0-9]{10}$/,
                                                    message: 'Phone number must be exactly 10 digits.'
                                                }
                                            })}
                                            onInput={(e) => handleInputChange(e, 10)}
                                            className={`form-control ${errors?.mobile_no ? 'is-invalid' : ''}`}
                                        />
                                        {errors?.phone && <p className="text-danger">{errors?.phone?.message}</p>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Company</label>
                                        <input type="text" className="form-control" {...register('company')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Address</label>
                                        <input type="text" className="form-control" {...register('address')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Country</label>
                                        <SelectTag
                                            name="country"
                                            control={control}
                                            label="Country"
                                            options={countryList}
                                            onChange={(selectedCountry) => {
                                                handleCountryChange(selectedCountry); // Fetch states based on selected country
                                                setValue("state", null); // Clear dependent fields
                                                setValue("city", null);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">State</label>
                                        <SelectTag
                                            name="state"
                                            control={control}
                                            label="State"
                                            options={stateList}
                                            onChange={(selectedState) => {
                                                handleStateChange(selectedState); // Fetch cities based on selected state
                                                setValue("city", null);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">City</label>
                                        <SelectTag
                                            name="city"
                                            control={control}
                                            label="City"
                                            options={cityList}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="form-group pb-4">
                                            <label className="form-label">Zip Code</label>
                                            <input
                                                maxLength={6}
                                                type="text"
                                                {...register('zipcode', {
                                                    required: "The zipcode field is required.",
                                                    minLength: {
                                                        value: 6,
                                                        message: "Zipcode must be longer than or equal to 6 numbers"
                                                    },
                                                    pattern: {
                                                        value: /^[0-9]+$/,
                                                        message: ""
                                                    }
                                                })}
                                                onInput={(e) => handleInputChange(e, 6)}
                                                className={`form-control ${errors?.zipcode ? 'is-invalid' : ''} `}
                                            />
                                            {errors?.zipcode && <div className="invalid-feedback">{errors?.zipcode?.message}</div>}
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="form-group pb-4">
                                            <label className='form-label'>No. of users</label>
                                            <input
                                                type="text"
                                                {...register('value', {
                                                    required: "The Lead value field is required.",
                                                    maxLength: {
                                                        value: 20,
                                                        message: "The Lead value must be at most 20 character."
                                                    },
                                                    pattern: {
                                                        value: /^[0-9]+$/,
                                                        message: "Only numeric values are allowed"
                                                      }
                                                })}
                                                onKeyDown={(e) => {
                                                    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
                                                        e.preventDefault(); // Prevent non-numeric input
                                                    }
                                                }}
                                                className={`form-control ${errors?.value ? 'is-invalid' : ''}`}
                                            />
                                            {errors?.value && <div className="invalid-feedback">{errors?.value?.message}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">No. of Employees</label>
                                        <SelectTag
                                            name="employee_count"
                                            isRequired={true}
                                            control={control}
                                            label="Employee Count"
                                            options={empoyeeCountOptions}
                                        />
                                        {errors?.employee_count && <small className="text-danger">{errors?.employee_count?.message}</small>}
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <label className="form-label">Designation</label>
                                        <input type="text" className="form-control" {...register('designation')} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Department</label>
                                        <input type="text" className="form-control" {...register('department')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Website</label>
                                        <input type="text" className="form-control" {...register('website')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Tags</label>
                                        <input type="text" className="form-control" {...register('tags')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Product</label>
                                        <Controller
                                            name="product_id"
                                            control={control}
                                            defaultValue={null}
                                            render={({ field }) => (
                                                <Select
                                                    options={productList}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => String(option.id)}
                                                    value={productList.find(option => option.id === field.value) || null}
                                                    onChange={(option) => field.onChange(option?.id || null)} // Sync value with form state
                                                    closeMenuOnSelect
                                                    isClearable
                                                />
                                            )}
                                        />

                                    </div>
                                    <div className="text-end">
                                        <button type="submit" className="btn btn-primary">Save</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditLead;
